import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { AlertTriangle, DollarSign, Users, Calendar, Shield, ExternalLink } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { SCAM_SEVERITY } from '../utils/scamDataAggregator';

// Fix Leaflet default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * Custom marker icons based on scam severity
 */
const createCustomIcon = (severity, verified) => {
  const colors = {
    [SCAM_SEVERITY.LOW]: '#10b981',      // green
    [SCAM_SEVERITY.MEDIUM]: '#f59e0b',   // amber
    [SCAM_SEVERITY.HIGH]: '#ef4444',     // red
    [SCAM_SEVERITY.CRITICAL]: '#dc2626', // dark red
  };

  const color = colors[severity] || colors[SCAM_SEVERITY.MEDIUM];
  const verifiedBadge = verified ? '<div class="verified-badge">✓</div>' : '';

  return L.divIcon({
    className: 'custom-scam-marker',
    html: `
      <div class="marker-pin" style="background-color: ${color};">
        <div class="marker-pulse" style="background-color: ${color};"></div>
        ${verifiedBadge}
      </div>
    `,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -42]
  });
};

/**
 * Format currency
 */
const formatCurrency = (amount) => {
  if (amount >= 1000000000) {
    return `$${(amount / 1000000000).toFixed(1)}B`;
  } else if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
};

/**
 * Format date
 */
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Map component that handles map interactions
 */
function MapController({ scams, selectedScam, onScamSelect }) {
  const map = useMap();

  useEffect(() => {
    if (selectedScam) {
      map.flyTo([selectedScam.lat, selectedScam.lng], 8, {
        duration: 1.5
      });
    }
  }, [selectedScam, map]);

  return null;
}

/**
 * Scam Marker Popup Component
 */
const ScamPopup = ({ scam }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      [SCAM_SEVERITY.LOW]: 'text-green-400',
      [SCAM_SEVERITY.MEDIUM]: 'text-amber-400',
      [SCAM_SEVERITY.HIGH]: 'text-red-400',
      [SCAM_SEVERITY.CRITICAL]: 'text-red-600',
    };
    return colors[severity] || colors[SCAM_SEVERITY.MEDIUM];
  };

  return (
    <div className="scam-popup min-w-[280px]">
      <div className="popup-header">
        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${getSeverityColor(scam.severity)}`} />
          {scam.title}
        </h3>
        {scam.verified && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
            <Shield className="w-3 h-3" />
            Verified
          </span>
        )}
      </div>

      <div className="popup-body mt-3 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(scam.severity)} bg-white/10`}>
            {scam.severity.toUpperCase()}
          </span>
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
            {scam.type}
          </span>
        </div>

        <p className="text-sm text-gray-300 line-clamp-3">{scam.description}</p>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
          {scam.amount > 0 && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <div>
                <div className="text-xs text-gray-400">Loss Amount</div>
                <div className="text-sm font-bold text-amber-400">
                  {formatCurrency(scam.amount)}
                </div>
              </div>
            </div>
          )}

          {scam.victimCount > 0 && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              <div>
                <div className="text-xs text-gray-400">Victims</div>
                <div className="text-sm font-bold text-blue-400">
                  {scam.victimCount.toLocaleString()}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 col-span-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-xs text-gray-400">Date Reported</div>
              <div className="text-sm text-gray-200">{formatDate(scam.date)}</div>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <a
            href={scam.url}
            target={scam.source !== 'CFE Reports' ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            View Details
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="text-xs text-gray-500 pt-1 border-t border-white/5">
          Source: {scam.source}
        </div>
      </div>
    </div>
  );
};

/**
 * Main GlobalScamMap Component
 */
const GlobalScamMap = ({ scams, selectedScam, onScamSelect, showHeatmap = false }) => {
  const [mapKey, setMapKey] = useState(0);

  // Default center (global view)
  const defaultCenter = [20, 0];
  const defaultZoom = 2;

  useEffect(() => {
    // Force map re-render when scams change significantly
    setMapKey(prev => prev + 1);
  }, [scams.length]);

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">
      <MapContainer
        key={mapKey}
        center={defaultCenter}
        zoom={defaultZoom}
        className="w-full h-full z-0"
        zoomControl={true}
        scrollWheelZoom={true}
      >
        {/* Dark theme tile layer */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />

        {/* Map controller for interactions */}
        <MapController
          scams={scams}
          selectedScam={selectedScam}
          onScamSelect={onScamSelect}
        />

        {/* Clustered markers */}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            let size = 'small';
            let className = 'marker-cluster-';

            if (count > 50) {
              size = 'large';
              className += 'large';
            } else if (count > 10) {
              size = 'medium';
              className += 'medium';
            } else {
              className += 'small';
            }

            return L.divIcon({
              html: `<div><span>${count}</span></div>`,
              className: 'marker-cluster ' + className,
              iconSize: L.point(40, 40)
            });
          }}
        >
          {scams.map((scam) => (
            <Marker
              key={scam.id}
              position={[scam.lat, scam.lng]}
              icon={createCustomIcon(scam.severity, scam.verified)}
              eventHandlers={{
                click: () => onScamSelect && onScamSelect(scam)
              }}
            >
              <Popup className="scam-popup-container">
                <ScamPopup scam={scam} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>

        {/* Optional heatmap circles */}
        {showHeatmap && scams.map((scam) => (
          <Circle
            key={`heat-${scam.id}`}
            center={[scam.lat, scam.lng]}
            radius={scam.amount ? Math.log10(scam.amount) * 50000 : 10000}
            pathOptions={{
              fillColor: scam.severity === SCAM_SEVERITY.CRITICAL ? '#dc2626' : '#ef4444',
              fillOpacity: 0.2,
              color: '#ef4444',
              weight: 1,
              opacity: 0.3
            }}
          />
        ))}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-black/60 backdrop-blur-md border border-white/10 rounded-lg p-3 text-sm">
        <div className="font-bold mb-2 text-white">Severity Levels</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-300">Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-gray-300">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-300">High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-600"></div>
            <span className="text-gray-300">Critical</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalScamMap;
